import { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Autocomplete,
  Box,
  Card,
  FormControl,
  Grid,
  InputAdornment,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { ArrowLeft, Briefcase, CalendarDays, Euro, Percent, Save } from 'lucide-react';
import { Button } from '../components/ui/button';
import api from '../services/api';

export const StyledCard = styled(Card)({
  boxShadow: '0 0 2px 0 rgba(145, 158, 171, 0.2), 0 12px 24px -4px rgba(145, 158, 171, 0.12)',
  borderRadius: 16,
  overflow: 'hidden'
});

export function buildEmptyForm(defaultStatoId = '') {
  return {
    cliente_id: '',
    tipo_operazione_id: '',
    stato_id: defaultStatoId ? String(defaultStatoId) : '',
    data_scadenza: '',
    data_recall: '',
    importo_richiesto: '',
    importo_finanziabile: '',
    importo_erogato: '',
    importo_rata: '',
    numero_rate: '',
    tan: '',
    taeg: '',
    istituto_erogante_id: '',
    data_erogazione: '',
    note: ''
  };
}

export function toDateInputValue(value) {
  if (!value) return '';
  const asString = String(value);
  return asString.length >= 10 ? asString.slice(0, 10) : asString;
}

export function normalizePraticaToForm(row, fallbackStatoId = '') {
  if (!row) return buildEmptyForm(fallbackStatoId);
  return {
    cliente_id: row.cliente_id ? String(row.cliente_id) : '',
    tipo_operazione_id: row.tipo_operazione_id ? String(row.tipo_operazione_id) : '',
    stato_id: row.stato_id ? String(row.stato_id) : fallbackStatoId ? String(fallbackStatoId) : '',
    data_scadenza: toDateInputValue(row.data_scadenza),
    data_recall: toDateInputValue(row.data_recall),
    importo_richiesto: row.importo_richiesto ?? '',
    importo_finanziabile: row.importo_finanziabile ?? '',
    importo_erogato: row.importo_erogato ?? '',
    importo_rata: row.importo_rata ?? '',
    numero_rate: row.numero_rate ?? '',
    tan: row.tan ?? '',
    taeg: row.taeg ?? '',
    istituto_erogante_id: row.istituto_erogante_id ? String(row.istituto_erogante_id) : '',
    data_erogazione: toDateInputValue(row.data_erogazione),
    note: row.note || ''
  };
}

export function buildPraticaPayload(formData) {
  return {
    cliente_id: formData.cliente_id || null,
    tipo_operazione_id: formData.tipo_operazione_id || null,
    stato_id: formData.stato_id || null,
    data_scadenza: formData.data_scadenza || null,
    data_recall: formData.data_recall || null,
    importo_richiesto: formData.importo_richiesto !== '' ? formData.importo_richiesto : null,
    importo_finanziabile: formData.importo_finanziabile !== '' ? formData.importo_finanziabile : null,
    importo_erogato: formData.importo_erogato !== '' ? formData.importo_erogato : null,
    importo_rata: formData.importo_rata !== '' ? formData.importo_rata : null,
    numero_rate: formData.numero_rate !== '' ? formData.numero_rate : null,
    tan: formData.tan !== '' ? formData.tan : null,
    taeg: formData.taeg !== '' ? formData.taeg : null,
    istituto_erogante_id: formData.istituto_erogante_id || null,
    data_erogazione: formData.data_erogazione || null,
    note: formData.note || null
  };
}

function numericAdornment(icon) {
  return (
    <InputAdornment position="start">
      {icon}
    </InputAdornment>
  );
}

function getClienteOptionLabel(option) {
  if (!option) return '';
  if (option.label) return String(option.label).trim();
  const nominativo = `${option.cognome || ''} ${option.nome || ''}`.trim();
  return nominativo || `Cliente #${option.id || ''}`;
}

function getClienteOptionDetails(option) {
  if (!option) return 'Cliente abilitato';
  return [option.codice_fiscale || null, option.telefono || null, option.email || null].filter(Boolean).join(' | ') || 'Cliente abilitato';
}

function getIstitutoOptionLabel(option) {
  if (!option) return '';
  return String(option.nome || '').trim() || `Istituto #${option.id || ''}`;
}

function getIstitutoOptionDetails(option) {
  if (!option) return 'Istituto erogante';
  return [option.codice || null, option.tipo || null, option.referente || null].filter(Boolean).join(' | ') || 'Istituto erogante';
}

export function PfPraticaFormLayout({
  title,
  subtitle,
  submitLabel,
  saving,
  error,
  formData,
  lookups,
  onFormChange,
  onSubmit,
  onCancel,
  onReset,
  backLabel = 'Indietro',
  disableClientFilter = false
}) {
  const clientiOptions = useMemo(() => {
    const currentId = String(formData.cliente_id || '');
    return (lookups.clienti || []).filter((row) => {
      if (disableClientFilter) return true;
      if (currentId && String(row.id) === currentId) return true;
      return Number(row.abilitato) === 1;
    });
  }, [disableClientFilter, formData.cliente_id, lookups.clienti]);

  const [clientSearchInput, setClientSearchInput] = useState('');
  const [asyncClientOptions, setAsyncClientOptions] = useState(clientiOptions);
  const [loadingClientOptions, setLoadingClientOptions] = useState(false);

  useEffect(() => {
    setAsyncClientOptions((prev) => {
      const merged = new Map();
      [...clientiOptions, ...prev].forEach((row) => {
        if (!row?.id) return;
        merged.set(String(row.id), row);
      });
      return Array.from(merged.values());
    });
  }, [clientiOptions]);

  useEffect(() => {
    let cancelled = false;
    const timeoutId = setTimeout(async () => {
      setLoadingClientOptions(true);
      try {
        const params = {
          page: 1,
          limit: 20,
          orderBy: 'cognome',
          orderDirection: 'asc'
        };

        const search = clientSearchInput.trim();
        if (search) params.search = search;
        if (!disableClientFilter) params.abilitato = '1';

        const response = await api.get('/pf-clienti', { params });
        const rows = Array.isArray(response.data?.data) ? response.data.data : [];
        if (cancelled) return;

        setAsyncClientOptions((prev) => {
          const merged = new Map();
          [...clientiOptions, ...rows, ...prev].forEach((row) => {
            if (!row?.id) return;
            merged.set(String(row.id), row);
          });
          return Array.from(merged.values());
        });
      } catch (_) {
        if (!cancelled) {
          setAsyncClientOptions(clientiOptions);
        }
      } finally {
        if (!cancelled) {
          setLoadingClientOptions(false);
        }
      }
    }, 300);

    return () => {
      cancelled = true;
      clearTimeout(timeoutId);
    };
  }, [clientSearchInput, clientiOptions, disableClientFilter]);

  const mergedClientOptions = useMemo(() => {
    const merged = new Map();
    [...clientiOptions, ...asyncClientOptions].forEach((row) => {
      if (!row?.id) return;
      merged.set(String(row.id), row);
    });
    return Array.from(merged.values());
  }, [asyncClientOptions, clientiOptions]);

  const selectedClienteOption = useMemo(() => {
    const currentId = String(formData.cliente_id || '');
    if (!currentId) return null;
    return mergedClientOptions.find((row) => String(row.id) === currentId) || null;
  }, [mergedClientOptions, formData.cliente_id]);

  const istitutiOptions = useMemo(() => {
    const currentId = String(formData.istituto_erogante_id || '');
    return (lookups.istituti || []).filter((row) => {
      if (currentId && String(row.id) === currentId) return true;
      return Number(row.attivo) === 1;
    });
  }, [formData.istituto_erogante_id, lookups.istituti]);

  const [istitutoSearchInput, setIstitutoSearchInput] = useState('');
  const [asyncIstitutoOptions, setAsyncIstitutoOptions] = useState(istitutiOptions);
  const [loadingIstitutoOptions, setLoadingIstitutoOptions] = useState(false);

  useEffect(() => {
    setAsyncIstitutoOptions((prev) => {
      const merged = new Map();
      [...istitutiOptions, ...prev].forEach((row) => {
        if (!row?.id) return;
        merged.set(String(row.id), row);
      });
      return Array.from(merged.values());
    });
  }, [istitutiOptions]);

  useEffect(() => {
    let cancelled = false;
    const timeoutId = setTimeout(async () => {
      setLoadingIstitutoOptions(true);
      try {
        const params = {
          limit: 20,
          attivo: 1
        };
        const search = istitutoSearchInput.trim();
        if (search) params.search = search;

        const response = await api.get('/pf-istituti-eroganti', { params });
        const rows = Array.isArray(response.data) ? response.data : [];
        if (cancelled) return;

        setAsyncIstitutoOptions((prev) => {
          const merged = new Map();
          [...istitutiOptions, ...rows, ...prev].forEach((row) => {
            if (!row?.id) return;
            merged.set(String(row.id), row);
          });
          return Array.from(merged.values());
        });
      } catch (_) {
        if (!cancelled) {
          setAsyncIstitutoOptions(istitutiOptions);
        }
      } finally {
        if (!cancelled) {
          setLoadingIstitutoOptions(false);
        }
      }
    }, 300);

    return () => {
      cancelled = true;
      clearTimeout(timeoutId);
    };
  }, [istitutiOptions, istitutoSearchInput]);

  const mergedIstitutoOptions = useMemo(() => {
    const merged = new Map();
    [...istitutiOptions, ...asyncIstitutoOptions].forEach((row) => {
      if (!row?.id) return;
      merged.set(String(row.id), row);
    });
    return Array.from(merged.values());
  }, [asyncIstitutoOptions, istitutiOptions]);

  const selectedIstitutoOption = useMemo(() => {
    const currentId = String(formData.istituto_erogante_id || '');
    if (!currentId) return null;
    return mergedIstitutoOptions.find((row) => String(row.id) === currentId) || null;
  }, [formData.istituto_erogante_id, mergedIstitutoOptions]);

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 2, mb: 3, flexWrap: 'wrap' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Button variant="outline" onClick={onCancel} className="gap-2">
            <ArrowLeft className="h-4 w-4" />
            {backLabel}
          </Button>
          <Box
            sx={{
              width: 40,
              height: 40,
              borderRadius: '50%',
              bgcolor: '#eef2ff',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            <Briefcase size={20} color="#4f46e5" />
          </Box>
          <Box>
            <Typography variant="h4" sx={{ fontWeight: 700 }}>
              {title}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {subtitle}
            </Typography>
          </Box>
        </Box>

        <Button onClick={onSubmit} disabled={saving} className="gap-2">
          <Save className="h-4 w-4" />
          {saving ? 'Salvataggio...' : submitLabel}
        </Button>
      </Box>

      {error ? (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      ) : null}

      <StyledCard>
        <Box component="form" onSubmit={onSubmit} sx={{ p: 3, display: 'grid', gap: 3 }}>
          <Box>
            <Typography variant="h6" sx={{ mb: 2, fontWeight: 700, letterSpacing: 0.2 }}>
              Dati base
            </Typography>
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <Autocomplete
                  fullWidth
                  size="small"
                  options={mergedClientOptions}
                  value={selectedClienteOption}
                  loading={loadingClientOptions}
                  inputValue={clientSearchInput}
                  onInputChange={(_, newInputValue) => setClientSearchInput(newInputValue)}
                  onChange={(_, newValue) =>
                    onFormChange({
                      target: {
                        name: 'cliente_id',
                        value: newValue ? String(newValue.id) : ''
                      }
                    })
                  }
                  getOptionLabel={(option) => getClienteOptionLabel(option)}
                  isOptionEqualToValue={(option, value) => String(option?.id || '') === String(value?.id || '')}
                  noOptionsText={clientSearchInput.trim() ? 'Nessun cliente trovato' : 'Inizia a digitare per cercare un cliente'}
                  renderOption={(props, option) => (
                    <li {...props}>
                      <Box sx={{ display: 'flex', flexDirection: 'column', minWidth: 0 }}>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          {getClienteOptionLabel(option)}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {getClienteOptionDetails(option)}
                        </Typography>
                      </Box>
                    </li>
                  )}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      required
                      label="Cliente (abilitato)"
                      placeholder="Cerca per nome, cognome, CF, telefono o email"
                    />
                  )}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <FormControl size="small" fullWidth required>
                  <InputLabel>Tipo operazione</InputLabel>
                  <Select label="Tipo operazione" name="tipo_operazione_id" value={formData.tipo_operazione_id} onChange={onFormChange}>
                    <MenuItem value="">Seleziona tipo operazione</MenuItem>
                    {(lookups.tipiOperazione || []).map((row) => (
                      <MenuItem key={row.id} value={String(row.id)}>
                        {row.nome_visualizzato}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12} md={4}>
                <FormControl size="small" fullWidth required>
                  <InputLabel>Stato pratica</InputLabel>
                  <Select label="Stato pratica" name="stato_id" value={formData.stato_id} onChange={onFormChange}>
                    <MenuItem value="">Seleziona stato</MenuItem>
                    {(lookups.stati || []).map((row) => (
                      <MenuItem key={row.id} value={String(row.id)}>
                        {row.nome_visualizzato}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  size="small"
                  label="Data scadenza"
                  name="data_scadenza"
                  type="date"
                  value={formData.data_scadenza}
                  onChange={onFormChange}
                  InputLabelProps={{ shrink: true }}
                  InputProps={{ startAdornment: numericAdornment(<CalendarDays size={16} />) }}
                />
              </Grid>

              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  size="small"
                  label="Data recall (opzionale)"
                  name="data_recall"
                  type="date"
                  value={formData.data_recall}
                  onChange={onFormChange}
                  InputLabelProps={{ shrink: true }}
                  InputProps={{ startAdornment: numericAdornment(<CalendarDays size={16} />) }}
                />
              </Grid>
            </Grid>
          </Box>

          <Box>
            <Typography variant="h6" sx={{ mb: 2, fontWeight: 700, letterSpacing: 0.2 }}>
              Dati finanziari
            </Typography>
            <Grid container spacing={3}>
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  size="small"
                  label="Importo richiesto (EUR)"
                  name="importo_richiesto"
                  type="number"
                  value={formData.importo_richiesto}
                  onChange={onFormChange}
                  placeholder="es. 15000"
                  inputProps={{ step: '0.01' }}
                  InputProps={{ startAdornment: numericAdornment(<Euro size={16} />) }}
                />
              </Grid>

              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  size="small"
                  label="Importo finanziabile (EUR)"
                  name="importo_finanziabile"
                  type="number"
                  value={formData.importo_finanziabile}
                  onChange={onFormChange}
                  placeholder="es. 18000"
                  inputProps={{ step: '0.01' }}
                  InputProps={{ startAdornment: numericAdornment(<Euro size={16} />) }}
                />
              </Grid>

              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  size="small"
                  label="Importo erogato (EUR)"
                  name="importo_erogato"
                  type="number"
                  value={formData.importo_erogato}
                  onChange={onFormChange}
                  placeholder="dopo approvazione"
                  inputProps={{ step: '0.01' }}
                  InputProps={{ startAdornment: numericAdornment(<Euro size={16} />) }}
                />
              </Grid>

              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  size="small"
                  label="Importo rata (EUR)"
                  name="importo_rata"
                  type="number"
                  value={formData.importo_rata}
                  onChange={onFormChange}
                  placeholder="es. 210"
                  inputProps={{ step: '0.01' }}
                  InputProps={{ startAdornment: numericAdornment(<Euro size={16} />) }}
                />
              </Grid>

              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  size="small"
                  label="Numero rate"
                  name="numero_rate"
                  type="number"
                  value={formData.numero_rate}
                  onChange={onFormChange}
                  placeholder="es. 120"
                />
              </Grid>

              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  size="small"
                  label="Tasso TAN (%)"
                  name="tan"
                  type="number"
                  value={formData.tan}
                  onChange={onFormChange}
                  placeholder="es. 8.50"
                  inputProps={{ step: '0.01' }}
                  InputProps={{ startAdornment: numericAdornment(<Percent size={16} />) }}
                />
              </Grid>

              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  size="small"
                  label="TAEG (%)"
                  name="taeg"
                  type="number"
                  value={formData.taeg}
                  onChange={onFormChange}
                  placeholder="es. 9.20"
                  inputProps={{ step: '0.01' }}
                  InputProps={{ startAdornment: numericAdornment(<Percent size={16} />) }}
                />
              </Grid>

              <Grid item xs={12} md={4}>
                <Autocomplete
                  fullWidth
                  size="small"
                  options={mergedIstitutoOptions}
                  value={selectedIstitutoOption}
                  loading={loadingIstitutoOptions}
                  inputValue={istitutoSearchInput}
                  onInputChange={(_, newInputValue) => setIstitutoSearchInput(newInputValue)}
                  onChange={(_, newValue) =>
                    onFormChange({
                      target: {
                        name: 'istituto_erogante_id',
                        value: newValue ? String(newValue.id) : ''
                      }
                    })
                  }
                  getOptionLabel={(option) => getIstitutoOptionLabel(option)}
                  isOptionEqualToValue={(option, value) => String(option?.id || '') === String(value?.id || '')}
                  noOptionsText={istitutoSearchInput.trim() ? 'Nessun istituto trovato' : 'Inizia a digitare per cercare un istituto'}
                  renderOption={(props, option) => (
                    <li {...props}>
                      <Box sx={{ display: 'flex', flexDirection: 'column', minWidth: 0 }}>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          {getIstitutoOptionLabel(option)}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {getIstitutoOptionDetails(option)}
                        </Typography>
                      </Box>
                    </li>
                  )}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Istituto erogante"
                      placeholder="Cerca per nome, codice, referente, telefono o email"
                    />
                  )}
                />
              </Grid>

              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  size="small"
                  label="Data erogazione"
                  name="data_erogazione"
                  type="date"
                  value={formData.data_erogazione}
                  onChange={onFormChange}
                  InputLabelProps={{ shrink: true }}
                  InputProps={{ startAdornment: numericAdornment(<CalendarDays size={16} />) }}
                />
              </Grid>
            </Grid>
          </Box>

          <Box>
            <Typography variant="h6" sx={{ mb: 2, fontWeight: 700, letterSpacing: 0.2 }}>
              Note operative
            </Typography>
            <TextField
              fullWidth
              multiline
              minRows={5}
              label="Note"
              name="note"
              value={formData.note}
              onChange={onFormChange}
              placeholder="Inserisci indicazioni operative, documenti mancanti, preferenze del cliente..."
            />
          </Box>

          <Box sx={{ display: 'flex', justifyContent: 'flex-start', gap: 1.5, pt: 1, flexWrap: 'wrap' }}>
            <Button type="submit" onClick={onSubmit} disabled={saving} className="gap-2">
              <Save className="h-4 w-4" />
              {saving ? 'Salvataggio...' : submitLabel}
            </Button>
            {onReset ? (
              <Button variant="outline" onClick={onReset} disabled={saving}>
                Reset campi
              </Button>
            ) : null}
            <Button variant="outline" onClick={onCancel} disabled={saving}>
              Annulla
            </Button>
          </Box>
        </Box>
      </StyledCard>
    </Box>
  );
}
